//IndividualRunsModal.tsx
import React from 'react'
import { Modal, Tabs, List, Card, Row, Col, Typography, Space, Table, Tag, Button } from 'antd'
const { Title, Text } = Typography

interface TokenInfo {
  input_tokens: number
  output_tokens: number
  total_tokens: number
  reasoning_tokens: number
  cached_tokens: number
  model: string
}

interface TranslationResult {
  model: string
  status: string
  parsed_response?: string
  response?: string
  token_info?: TokenInfo
}

interface ModelResult {
  model: string
  status: 'success' | 'error'
  response: string
  token_info?: TokenInfo
}

interface IndividualRunDetail {
  runNumber: number
  translation: TranslationResult
  evaluations: ModelResult[]
}

interface MultiRunAggregatedResult {
  testName: string
  language: string
  instructionId: string
  textId: string
  models: string[]
  totalRuns: number
  aggregatedTranslations: Record<string, any>
  aggregatedEvaluations: Record<string, any>
  individualRuns: IndividualRunDetail[]
}

type EvalCategory = { score: number; comment: string }
type EvalJson = Record<string, EvalCategory>

interface IndividualRunsModalProps {
  visible: boolean
  onClose: () => void
  multiRunResults: MultiRunAggregatedResult | null
  parseEvaluationResult: (response: string) => EvalJson | null
  hasAllInvalidScores: (evalResult: EvalJson | null) => boolean
  isValidScore: (score: number) => boolean
  getScoreColor: (score: number) => string
  calculateCost: (model: string, tokenInfo: TokenInfo) => number
  onShowTranslation: (content: string) => void
}

const IndividualRunsModal: React.FC<IndividualRunsModalProps> = ({
  visible,
  onClose,
  multiRunResults,
  parseEvaluationResult,
  hasAllInvalidScores,
  isValidScore,
  getScoreColor,
  calculateCost,
  onShowTranslation,
}) => {
  return (
    <Modal
      title={`Individual Run Details - ${multiRunResults?.testName || ''}`}
      visible={visible}
      onCancel={onClose}
      width={1400}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {multiRunResults && (
        <Tabs>
          {multiRunResults.models.map(modelName => {
            const modelRuns = multiRunResults.individualRuns.filter(
              run => run.translation.model === modelName
            )

            return (
              <Tabs.TabPane key={modelName} tab={modelName}>
                <List
                  dataSource={modelRuns}
                  renderItem={(run) => {
                    const overallScores: Record<string, number> = {}
                    const detailedEvaluations: Record<string, EvalJson> = {}

                    run.evaluations.forEach(evalResult => {
                      const [, evalModel] = evalResult.model.split('::', 2)
                      const evalJson = parseEvaluationResult(evalResult.response)
                      if (evalJson && !hasAllInvalidScores(evalJson)) {
                        detailedEvaluations[evalModel] = evalJson
                        const validScores = Object.values(evalJson)
                          .filter(cat => isValidScore(cat.score))
                          .map(cat => cat.score)
                        if (validScores.length > 0) {
                          overallScores[evalModel] =
                            validScores.reduce((a, b) => a + b, 0) / validScores.length
                        }
                      }
                    })

                    return (
                      <List.Item style={{ display: 'block' }}>
                        <Card size="small" style={{ marginBottom: 16 }}>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Title level={5}>Run {run.runNumber}</Title>
                              <Space direction="vertical">
                                <div>
                                  <Text strong>Token Usage:</Text>
                                  {run.translation.token_info && (
                                    <div>
                                      <Text>
                                        Input: {run.translation.token_info.input_tokens}
                                      </Text>
                                      <br />
                                      <Text>
                                        Output: {run.translation.token_info.output_tokens}
                                      </Text>
                                      <br />
                                      <Text>
                                        Total: {run.translation.token_info.total_tokens}
                                      </Text>
                                      <br />
                                      <Text>
                                        Cost: $
                                        {calculateCost(
                                          modelName,
                                          run.translation.token_info
                                        ).toFixed(5)}
                                      </Text>
                                    </div>
                                  )}
                                </div>
                                {(() => {
                                  const evalTotal = run.evaluations.reduce((sum, er) => {
                                    const [, eModel] = er.model.split('::', 2)
                                    if (er.token_info) {
                                      try {
                                        return sum + calculateCost(eModel, er.token_info)
                                      } catch {
                                        return sum
                                      }
                                    }
                                    return sum
                                  }, 0)
                                  const translationCost = run.translation.token_info
                                    ? calculateCost(modelName, run.translation.token_info)
                                    : 0
                                  const totalRunCost = translationCost + evalTotal
                                  return (
                                    <div>
                                      <Text>
                                        Evaluation Cost: ${evalTotal.toFixed(5)}
                                      </Text>
                                      <br />
                                      <Text strong>
                                        Total Run Cost: ${totalRunCost.toFixed(5)}
                                      </Text>
                                    </div>
                                  )
                                })()}
                                <Button
                                  size="small"
                                  onClick={() =>
                                    onShowTranslation(
                                      run.translation.parsed_response || 'Translation not found.'
                                    )
                                  }
                                >
                                  View Translation
                                </Button>
                              </Space>
                            </Col>
                            <Col span={16}>
                              <Title level={5}>Evaluation Details</Title>
                              <Tabs size="small">
                                {Object.entries(detailedEvaluations).map(([evalModel, evalData]) => (
                                  <Tabs.TabPane
                                    key={evalModel}
                                    tab={
                                      <Space>
                                        {evalModel}
                                        <Tag color={getScoreColor(overallScores[evalModel] || 0)}>
                                          {(overallScores[evalModel] || 0).toFixed(2)}
                                        </Tag>
                                      </Space>
                                    }
                                  >
                                    {(() => {
                                      // Show evaluator token usage and cost (if token_info present) for this evaluator
                                      const erRec = run.evaluations.find(er => {
                                        const [, eModel] = er.model.split('::', 2)
                                        return eModel === evalModel
                                      })
                                      const ti = erRec?.token_info
                                      if (!ti) return null
                                      const cached = ti.cached_tokens || 0
                                      const nonCached = (ti.input_tokens || 0) - cached
                                      const reasoning = ti.reasoning_tokens || 0
                                      const nonReasoningOut = (ti.output_tokens || 0) - reasoning
                                      let costStr = ''
                                      try {
                                        costStr = calculateCost(evalModel, ti).toFixed(5)
                                      } catch {
                                        costStr = '0.00000'
                                      }
                                      return (
                                        <div style={{ marginBottom: 8, fontSize: 12 }}>
                                          <div><strong>Evaluator Token Usage</strong></div>
                                          <div>Input (non-cached): {nonCached} • Cached: {cached}</div>
                                          <div>Output (non-reasoning): {nonReasoningOut} • Reasoning: {reasoning}</div>
                                          <div>Total: {ti.total_tokens ?? ((ti.input_tokens || 0) + (ti.output_tokens || 0))}</div>
                                          <div><strong>Evaluator Cost:</strong> ${costStr}</div>
                                        </div>
                                      )
                                    })()}

                                    <Table
                                      size="small"
                                      pagination={false}
                                      dataSource={Object.entries(evalData).map(([category, data]) => ({
                                        key: category,
                                        category,
                                        score: data.score,
                                        comment: data.comment,
                                        isValid: isValidScore(data.score)
                                      }))}
                                      columns={[
                                        {
                                          title: 'Category',
                                          dataIndex: 'category',
                                          key: 'category',
                                          width: '20%',
                                          render: (text: string) => <Text strong>{text}</Text>,
                                        },
                                        {
                                          title: 'Score',
                                          dataIndex: 'score',
                                          key: 'score',
                                          width: '15%',
                                          align: 'center' as const,
                                          render: (score: number, record: any) => (
                                            <Tag color={record.isValid ? getScoreColor(score) : 'default'}>
                                              {score} {!record.isValid && '(excluded)'}
                                            </Tag>
                                          ),
                                        },
                                        {
                                          title: 'Comment',
                                          dataIndex: 'comment',
                                          key: 'comment',
                                          width: '65%',
                                          render: (comment: string) => (
                                            <Text style={{ fontSize: '12px' }}>{comment}</Text>
                                          ),
                                        },
                                      ]}
                                    />
                                  </Tabs.TabPane>
                                ))}
                              </Tabs>
                            </Col>
                          </Row>
                        </Card>
                      </List.Item>
                    )
                  }}
                />
              </Tabs.TabPane>
            )
          })}
        </Tabs>
      )}
    </Modal>
  )
}

export default IndividualRunsModal
